"use client";

import { useState } from "react";

import type { PublicTeam } from "@/lib/api";

import RosterItem from "./RosterItem";

type RosterPlayer = {
  id: string;
  displayName: string;
  profileImageUrl?: string | null;
  isCaptain: boolean;
};

type RosterComponentProps = {
  team: PublicTeam | null;
  roster: RosterPlayer[];
  faction: "asmodian" | "elyos";
  legionName: string;
};

function rosterRegionLabel(region: string | undefined) {
  const value = (region ?? "latam").trim().toLowerCase();
  if (value === "latam") return "Latam";
  if (value === "na") return "NA";
  if (value === "eu") return "EU";
  if (!region) return "Latam";
  return region.charAt(0).toUpperCase() + region.slice(1);
}

const RosterComponent = ({
  team,
  roster,
  faction,
  legionName,
}: RosterComponentProps) => {
  const [showAll, setShowAll] = useState(false);

  const captain = roster.find((creator) => creator.isCaptain) ?? roster[0] ?? null;
  const regionLabel = rosterRegionLabel(team?.region);
  const frameSrc =
    faction === "elyos"
      ? "/assets/roster/captain-frame-elyos.svg"
      : "/assets/roster/captain-frame-asmodian.svg";
  const flourishSrc =
    faction === "elyos"
      ? "/assets/roster/captain-flourish-elyos.svg"
      : "/assets/roster/captain-flourish-asmodian.svg";

  const leftRoster = roster.slice(0, 5);
  const rightRoster = roster.slice(5, 10);

  return (
    <section className={`cs-team-roster cs-team-roster-${faction}`}>
      {/* MOBILE CAPTAIN */}
      <div className="cs-team-roster-mobile-captain">
        <div className="cs-team-roster-mobile-avatar">
          {captain?.profileImageUrl ? (
            <img src={captain.profileImageUrl} alt="" />
          ) : (
            <span>{captain?.displayName?.charAt(0) ?? "?"}</span>
          )}
        </div>

        <div className="cs-team-roster-mobile-captain-info">
          <p className="cs-team-roster-mobile-meta">
            <span>
              {team?.faction
                ? `${team.faction.toUpperCase()} FACTION`
                : `${faction.toUpperCase()} FACTION`}
            </span>

            <span aria-hidden="true">·</span>

            <span>{legionName.toUpperCase()}</span>
          </p>

          <h3>
            {captain?.displayName ?? "TEAM CAPTAIN NAME"}
          </h3>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="cs-team-roster-header">
        <h2>TEAM ROSTER</h2>

        <p>
          {roster.length} cyber athletes fighting for Atreia
        </p>
      </div>

      {/* MOBILE LABEL */}
      <p className="cs-team-roster-mobile-title">
        TEAM ROSTER
      </p>

      <div className="cs-team-roster-content">
        {/* FIRST FIVE */}
        <div className="cs-team-roster-column cs-team-roster-column-first">
          {leftRoster.map((player, index) => (
            <RosterItem
              key={player.id}
              player={player}
              number={index + 1}
            />
          ))}
        </div>

        {/* DESKTOP CAPTAIN CARD */}
        <div className="cs-team-roster-captain">
          <img
            className="cs-team-roster-captain-frame"
            src={frameSrc}
            alt=""
            width={382}
            height={336}
          />

          <div className="cs-team-captain-content">
            <div className="cs-team-captain-identity">
              <p className="cs-team-roster-region">{regionLabel}</p>

              <div className="cs-team-captain-portrait">
                <div className="cs-team-roster-captain-avatar">
                  {captain?.profileImageUrl ? (
                    <img src={captain.profileImageUrl} alt="" />
                  ) : (
                    <span>{captain?.displayName?.charAt(0) ?? "?"}</span>
                  )}
                </div>

                <h2 className="cs-team-roster-captain-name">
                  {captain?.displayName ?? "NAME"}
                </h2>
              </div>
            </div>

            <div className="cs-team-captain-mark">
              <img
                className="cs-team-roster-captain-flourish"
                src={flourishSrc}
                alt=""
                width={294}
                height={34}
              />

              <p className="cs-team-roster-faction">
                {faction.toUpperCase()}
              </p>
            </div>
          </div>
        </div>

        {/* SECOND FIVE */}
        <div
          className={`cs-team-roster-column cs-team-roster-column-second ${
            showAll ? "is-visible" : ""
          }`}
        >
          {rightRoster.map((player, index) => (
            <RosterItem
              key={player.id}
              player={player}
              number={index + 6}
            />
          ))}
        </div>
      </div>

      {/* MOBILE VIEW MORE */}
      {rightRoster.length > 0 && (
        <button
          type="button"
          className="cs-team-roster-view-more"
          onClick={() => setShowAll((current) => !current)}
          aria-expanded={showAll}
        >
          {showAll ? "VIEW LESS" : "VIEW MORE"}
        </button>
      )}
    </section>
  );
};

export default RosterComponent;